import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

function Programs() {
    const implementationSteps = [
        {
            icon: 'mdi:clipboard-text',
            title: 'Assessment',
            description: 'Community needs analysis and stakeholder consultation'
        },
        {
            icon: 'mdi:draw',
            title: 'Design',
            description: 'Evidence-based program development with local input'
        },
        {
            icon: 'mdi:rocket-launch',
            title: 'Launch',
            description: 'Pilot implementation with continuous monitoring'
        },
        {
            icon: 'mdi:chart-line',
            title: 'Scale',
            description: 'Expansion based on data and community feedback'
        },
        {
            icon: 'mdi:update',
            title: 'Sustain',
            description: 'Long-term support and continuous improvement'
        }
    ];

    return (
        <div className="programs-page">
            {/* Page Header */}
            <section className="page-header programs-header">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            <h1 className="page-title animate-slide-up">Our Programs</h1>
                            <p className="page-subtitle animate-slide-up delay-1">
                                Innovative initiatives designed to create lasting impact in health and education
                            </p>
                        </div>
                        <div className="col-lg-4">
                            <div className="header-graphic animate-fade-in delay-2">
                                <Icon icon="carbon:program" className="header-icon" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Implementation Model */}
            <section className="implementation-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-label">How We Work</span>
                        <h2 className="section-title">Our Implementation Model</h2>
                        <p className="section-description">
                            A systematic, community-centered approach to creating sustainable impact
                        </p>
                    </div>

                    <div className="implementation-steps">
                        {implementationSteps.map((step, index) => (
                            <div key={index} className="implementation-step">
                                <div className="step-number">{index + 1}</div>
                                <div className="step-icon">
                                    <Icon icon={step.icon} />
                                </div>
                                <h4>{step.title}</h4>
                                <p>{step.description}</p>
                                {index < implementationSteps.length - 1 && (
                                    <div className="step-connector"></div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="implementation-details">
                        <div className="row g-4">
                            <div className="col-lg-4">
                                <div className="detail-card">
                                    <Icon icon="mdi:account-group" className="detail-icon" />
                                    <h4>Community Partnership</h4>
                                    <p>
                                        We work alongside communities, ensuring their voices guide every decision
                                        and programs reflect their unique needs and aspirations.
                                    </p>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="detail-card">
                                    <Icon icon="mdi:chart-box" className="detail-icon" />
                                    <h4>Data-Driven Approach</h4>
                                    <p>
                                        Rigorous monitoring and evaluation ensure our programs deliver measurable
                                        results and continuously improve based on evidence.
                                    </p>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="detail-card">
                                    <Icon icon="mdi:sync" className="detail-icon" />
                                    <h4>Sustainable Solutions</h4>
                                    <p>
                                        Programs are designed for long-term sustainability, building local capacity
                                        and creating systems that endure beyond our direct involvement.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HSCF-owned Initiatives */}
            <section className="initiatives-section owned-initiatives">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-label">Direct Impact</span>
                        <h2 className="section-title">HSCF-owned Initiatives</h2>
                        <p className="section-description">
                            Programs developed and implemented directly by HSCF
                        </p>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <Link to="/programs/educateher" className="initiative-card educateher-card">
                                <div className="initiative-background"></div>
                                <div className="initiative-content">
                                    <div className="initiative-icon">
                                        <Icon icon="mdi:school" />
                                    </div>
                                    <div className="initiative-info">
                                        <h3>EducateHer</h3>
                                        <p>
                                            Through EducateHer, HSC Foundation is partnering with community leaders, teachers, parents, and local associations to strengthen girls' school attendance, reduce dropout rates, and promote pathways to leadership for young women.
                                        </p>
                                        {/*
                                        <div className="initiative-stats">
                                        <div className="stat">
                                            <strong>500+</strong>
                                            <span>Beneficiaries</span>
                                        </div>
                                        <div className="stat">
                                            <strong>85%</strong>
                                            <span>Completion Rate</span>
                                        </div>
                                        <div className="stat">
                                            <strong>15+</strong>
                                            <span>Partner Schools</span>
                                        </div>
                                        </div>
                                        */}
                                    </div>
                                    <div className="initiative-arrow">
                                        <Icon icon="mdi:arrow-right" />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Signature White Label Initiatives */}
            <section className="initiatives-section white-label-initiatives">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-label">Specialized Programs</span>
                        <h2 className="section-title">Signature White Label Initiatives</h2>
                        <p className="section-description">
                            Prestigious awards and fellowships recognizing excellence and innovation
                        </p>
                    </div>

                    <div className="row g-4">
                        <div className="col-lg-6">
                            <Link to="/programs/rtygia" className="initiative-card rtygia-card">
                                <div className="initiative-background"></div>
                                <div className="initiative-content">
                                    <div className="initiative-icon">
                                        <Icon icon="mdi:map-marker-path" />
                                    </div>
                                    <div className="initiative-info">
                                        <h3>Route Tracers YSN Geospatial Innovation Award (RTYGIA)</h3>
                                        <p>
                                            The Route Tracers YSN Geospatial Innovation Award is a 5-year endowed initiative designed to identify, support, and scale innovative, scalable, and wide-ranging ideas in the field of surveying, geographic information systems, remote sensing, and environmental management, developed by young surveyors (aged 35 years and below) who are members of the Young Surveyors Network, Nigeria.
                                        </p>
                                        <div className="initiative-features">
                                            <div className="feature">
                                                <Icon icon="mdi:trophy" />
                                                <span>Annual Award</span>
                                            </div>
                                            <div className="feature">
                                                <Icon icon="mdi:cash" />
                                                <span>Research Funding</span>
                                            </div>
                                            <div className="feature">
                                                <Icon icon="mdi:account-school" />
                                                <span>Mentorship</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="initiative-arrow">
                                        <Icon icon="mdi:arrow-right" />
                                    </div>
                                </div>
                            </Link>
                        </div>

                        <div className="col-lg-6">
                            <Link to="#" className="initiative-card aikpokpodion-card">
                                <div className="initiative-background"></div>
                                <div className="initiative-content">
                                    <div className="initiative-icon">
                                        <Icon icon="mdi:dna" />
                                    </div>
                                    <div className="initiative-info">
                                        <h3>Prof. P. Aikpokpodion Genetics and Biotechnology Innovation Fellowship</h3>
                                        <p>
                                            Supporting groundbreaking research in genetics and biotechnology,
                                            honoring the legacy of Prof. Aikpokpodion's contributions to science.
                                        </p>
                                        <div className="initiative-features">
                                            <div className="feature">
                                                <Icon icon="mdi:info" />
                                                <span>Coming soon</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="initiative-arrow">
                                        <Icon icon="mdi:arrow-right" />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            {/*
            <section className="programs-cta">
                <div className="container">
                <div className="cta-content text-center">
                    <h2>Support Our Programs</h2>
                    <p>
                    Your contribution helps us expand these life-changing initiatives to more communities
                    </p>
                    <div className="cta-buttons">
                    <button className="btn btn-cta-primary">
                        <Icon icon="mdi:hand-heart" className="me-2" />
                        Donate to a Program
                    </button>
                    <Link to="/contact" className="btn btn-cta-secondary text-dark">
                        Partner With Us
                    </Link>
                    </div>
                </div>
                </div>
            </section>
            */}
        </div>
    );
}

export default Programs;
